import { ReactNode, useRef } from 'react';

import { Box, Flex, FlexProps, Heading, Link, Skeleton, Tag, Text } from '@chakra-ui/react';
import { useResizeObserver } from '@react-hookz/web';

import { Markdown } from '~/components/markdown';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { FeatureList } from '~/features/plans/components/feature-list';
import { PriceAndUnitPrice } from '~/features/plans/components/price-and-unit-price';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';

import { PlanMetadata } from '../models/plan-metadata';

import { PlanPriceCardLayout } from './individual-plans-section-v2/plan-price-card-layout';

export type TeamPricePlanProps = {
  containerProps?: FlexProps;
  metadata: PlanMetadata;
  button: ReactNode;
  billingFrequencyRadioSlot?: ReactNode;
  setPriceHeight?: (newHeight: number) => void;
  syncRenderedPriceHeight?: boolean;
};

export function TeamPricePlan({
  containerProps,
  metadata,
  button,
  setPriceHeight,
  syncRenderedPriceHeight,
}: TeamPricePlanProps) {
  const { currency } = useCurrency();
  const { data: pricePlans } = usePricePlans(currency);
  const ref = useRef<HTMLDivElement>(null);

  useResizeObserver(
    ref,
    (entry) => {
      const height = entry.target.getBoundingClientRect().height;
      setPriceHeight?.(height);
    },
    syncRenderedPriceHeight
  );
  const teamPlan = pricePlans?.prices['monthly']?.team;
  const { displayPrice, displayPriceUnits } = teamPlan || {};
  const tags = metadata?.tags;
  function renderPrice() {
    if (!displayPrice && !displayPriceUnits) {
      return (
        <Flex flexDirection='column' gap='4px'>
          <Skeleton height='48px' width='84px' />
        </Flex>
      );
    } else {
      return <PriceAndUnitPrice displayPrice={displayPrice} displayPriceUnits={displayPriceUnits} />;
    }
  }
  return (
    <PlanPriceCardLayout {...containerProps}>
      <Flex flex='1' flexDir='column' gap={6}>
        <Flex
          flexDir='column'
          gap={6}
          sx={{
            '&::after': {
              content: "''",
              borderBottom: '1px solid',
              borderBottomColor: 'gray.600',
              opacity: '0.15',
            },
          }}
        >
          <Box flexBasis='var(--max-price-height)' flexGrow='1' flexShrink='0'>
            <Flex ref={ref} flexDir='column' gap={6}>
              <Flex alignItems='flex-start' gap='6px' justifyContent='space-between'>
                <Box flex='1'>
                  <Heading as='p' display='inline-block' size='marketing-xl'>
                    {metadata.title}
                  </Heading>
                  <Text>{metadata.numberOfUsers}</Text>
                </Box>
                {tags?.[0] && <Tag>{tags[0]}</Tag>}
              </Flex>
              <Box>{renderPrice()}</Box>
            </Flex>
          </Box>
          <Box>{button}</Box>
        </Flex>
        <Flex flex='1' flexDir='column' gap={4}>
          <FeatureList features={metadata.features} />
          {metadata.footnote && (
            <Box mt='auto'>
              <Markdown
                markdownText={metadata.footnote}
                components={{
                  p: (props) => <Text {...props} color='gray.700' fontSize='xs' />,
                  a: (props) => <Link {...props} color='gray.700' fontSize='xs' />,
                }}
              />
            </Box>
          )}
        </Flex>
      </Flex>
    </PlanPriceCardLayout>
  );
}
